<template>
  <div
    class="modelaudio-drag-upload"
    :class="{
      [`modelaudio-drag-upload--${createRenderType}`]: true,
    }"
  >
    <file-pond
      ref="refUpload"
      v-if="renderData.ifRenderFilePond"
      label-idle="歌曲文件"
      v-bind:allow-multiple="true"
      :acceptedFileTypes="renderData.acceptedFileTypes.join(',')"
      @addfile="handleAddFile"
      v-bind:files="renderData.files"
    >
    </file-pond>

    <div class="modelaudio-drag-upload-main" ref="refUploadContent">
      <div
        v-if="!uploadList.length && !loraUploadList.length"
        class="empty-wrapper"
      >
        <div class="upload-title flex-center">
          <img src="@/assets/img/lora/drag-ico.png" alt="" />
          <div class="text" v-html="$$t('create.drag_upload_song_files')"></div>
        </div>
        <div class="upload-issue">
          <div class="flex item" v-for="item in issueList" :key="item.name">
            <div class="name tr">{{ item.name }}:</div>
            <div class="desc">{{ item.desc }}</div>
          </div>
        </div>
        <div class="upload-btn flex-center">
          {{ $$t("create.upload_files") }}
        </div>
      </div>
      <div v-else class="height-all">
        <PullTo
          :disabledRefresh="true"
          ref="pullRef"
          v-model:loadingMore="loraRnderData.loadingMore"
          v-model:isLoading="loraRnderData.isLoading"
          v-model:finished="loraRnderData.finished"
          :datas="loraUploadList"
          :showInfo="showInfo"
          @loadMore="doLoadMore"
        >
          <template #customContent>
            <div class="flex show-list">
              <div class="card card-add flex-center">
                <img src="@/assets/img/lora/add.png" alt="" />
              </div>
              <div class="card" v-for="item in uploadList" :key="item.uuid">
                <AudioCard
                  :data="item"
                  @deleteUpload="deleteUpload(item.uuid, 'upload')"
                  @togglePlay="doTogglePlay"
                  @retryUpload="doRetryUpload(item.uuid)"
                ></AudioCard>
              </div>
              <div class="card" v-for="item in loraUploadList" :key="item.id">
                <AudioCard
                  :data="item"
                  @deleteUpload="deleteUpload(item.id, 'saved')"
                  @togglePlay="doTogglePlay"
                ></AudioCard>
              </div>
            </div>
          </template>
        </PullTo>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, nextTick, getCurrentInstance } from "vue";
import { storeToRefs } from "pinia";
import vueFilePond from "vue-filepond";
import "filepond/dist/filepond.min.css";
import objectStorage from "@/lib/objectStorage/objectStorage.js";
import tools from "@/utils/tools.js";
import useCreateLayoutStore from "@/store/create/createLayoutStore.js";
import useModelDataStore from "@/store/model/modelDataStore.js";
import ffmpegUtil from "@/components/media/utils/ffmpegUtil.js";
import message from "@/components/functionCallComponent/message.jsx";
import clipUtil from "@/components/media/utils/clipUtil.js";
import PullTo from "@/components/basic/pullTo/PullTo.vue";
import AudioCard from "./audioCard.vue";

const globalProperties =
  getCurrentInstance()?.appContext.config.globalProperties;
const { $$t } = globalProperties?.$i18n;
const modelDataStore = useModelDataStore();
const createLayoutStore = useCreateLayoutStore();
const { createRenderType } = storeToRefs(createLayoutStore);
const { loraRnderData, uploadList, loraUploadList, show_total } =
  storeToRefs(modelDataStore);

const FilePond = vueFilePond();

const emits = defineEmits([
  "saveSuccess",
  "syncUploadState",
  "initEditData",
  "addId",
  "deleteUpload",
  "retryUpload",
]);

const issueList = [
  {
    name: $$t("create.duration"),
    desc: $$t("create.duration_desc"),
  },
  {
    name: $$t("create.quality"),
    desc: $$t("create.quality_desc"),
  },
  {
    name: $$t("create.song_style"),
    desc: $$t("create.song_style_desc"),
  },
  {
    name: $$t("create.format"),
    desc: $$t("create.format_desc"),
  },
  {
    name: $$t("create.copyright"),
    desc: $$t("create.copyright_desc"),
  },
];

const pullRef = ref(null);
const refUpload = ref(null);
const refUploadContent = ref(null);
const showInfo = reactive({
  networkerror: $$t("common.network_error"),
  loadfailure: $$t("common.load_failure"),
  contentviolation: $$t("common.content_violation"),
  emptydata: $$t("common.empty_data"),
});

const acceptedFileTypes = [
  "audio/x-wav",
  "video/quicktime",
  "video/mp4",
  "audio/wav",
  "audio/x-m4a",
  "audio/flac",
  "audio/mpeg",
  ".mp3",
  ".wav",
  ".m4a",
  ".mp4",
  ".mov",
  ".MP4",
  ".flac",
];

const renderData = reactive({
  ifRenderFilePond: true,
  files: [],
  file: null,
  acceptedFileTypes: acceptedFileTypes,
  editFilePopupVisible: false,
  clipSourceData: null,
  editFileName: "",
});

const handleQueue = ref([]); // 存储待处理的文件队列
const handling = ref(false); // 是否正在处理标志

const minDuration = 30;
const maxDuration = 270;
const maxFileNumber = 200;

const doRetryUpload = (id) => {
  // emits("retryUpload");
  modelDataStore.retryUploadItem(id).then((res) => {
    if (res.state == 1) {
      modelDataStore.updateFileList(id, { is_fail: false });
      doUploadFile(res.file, id);
    } else if (res.state == 2) {
      modelDataStore.updateFileList(id, { is_fail: false });
      emits("initEditData", id);
    }
  });
};

const deleteUpload = (id, type) => {
  emits("deleteUpload", id, type, () => {
    // alert("8888")
    // refMescroll.value?.mescroll?.triggerUpScroll();
    pullRef.value && pullRef.value.triggerUpScroll();
  });
};

const doTogglePlay = (isPlay) => {
  emits("togglePlay", isPlay);
};

const checkMediaType = (file) => {
  if (file.type.toLocaleLowerCase().startsWith("audio")) {
    return acceptedFileTypes.includes(file.type.toLocaleLowerCase())
      ? "audio"
      : "unknown";
  } else if (file.type.toLocaleLowerCase().startsWith("video")) {
    return acceptedFileTypes.includes(file.type.toLocaleLowerCase())
      ? "video"
      : "unknown";
  } else {
    return "unknown";
  }
};

// 初始化上传内容
const initUploadContent = () => {
  if (refUploadContent.value) {
    nextTick(() => {
      refUpload.value?.$el
        .querySelector(".filepond--drop-label")
        .appendChild(refUploadContent.value);
    });
    setTimeout(() => {
      refUpload.value?.$el
        ?.querySelector("input")
        ?.setAttribute("accept", renderData.acceptedFileTypes.join(","));
    }, 20);
  }
};

// 处理文件添加
const handleAddFile = (error, fileItem, fileList) => {
  if (error || !fileItem) {
    console.error("File add error:", error);
    return;
  }

  const number = modelDataStore.getFileNumber();
  const filePondInstance = refUpload.value;

  // 检查文件数量是否超过限制
  if (number >= maxFileNumber) {
    filePondInstance.removeFile(fileItem);
    return;
  }

  // 生成唯一 ID 并添加到文件列表
  const uuid = tools.getUuid();
  modelDataStore.addFileList({
    uuid,
    state: 1,
    file: fileItem.file,
    name: fileItem.file.name?.replace(/.[a-zA-Z0-9]+$/, "").slice(0, 50),
  });

  // 将文件添加到处理队列
  handleQueue.value.push({
    file: fileItem.file,
    uuid,
  });

  // 开始处理队列
  processHandleQueue();
};

// 处理单个文件（包含文件检查和上传）

const handleAfterRead = (file, uuid) => {
  return new Promise(async (resolve, reject) => {
    try {
      // 1. 检查文件
      const checkedFile = await checkFile(file, uuid);

      // 2. 上传文件
      const uploadResult = await doUploadFile(checkedFile.file, uuid);

      if (uploadResult.success) {
        // // 上传成功，返回结果
        resolve({
          file: checkedFile.file,
          uuid,
          uploadResult: uploadResult.result,
        });
      } else {
        // 上传失败，返回错误
        reject(uploadResult.error);
      }
    } catch (error) {
      // 如果文件检查失败，返回错误
      reject(error);
    }
  });
};

const processHandleQueue = async () => {
  if (handling.value) return; // 如果正在处理，直接返回
  if (handleQueue.value.length === 0) return; // 没有待处理的文件

  handling.value = true; // 标记为正在处理

  // 每次处理 10 个文件
  const maxBatchSize = createRenderType.value == "mobile" ? 3 : 5;
  const batch = handleQueue.value.splice(0, maxBatchSize);

  try {
    await Promise.all(
      batch.map(({ file, uuid }) =>
        handleAfterRead(file, uuid)
          .then((result) => {
            emits("initEditData", uuid);
            console.log("File processed and uploaded:", result);
          })
          .catch((error) => {
            console.error("File handle or upload error:", error);
          })
      )
    );
  } catch (error) {
    console.error("Batch process error:", error);
  }

  handling.value = false; // 标记处理完成

  // 继续处理队列中的剩余文件
  setTimeout(() => {
    processHandleQueue();
  }, 200);
};

// 检查文件
const checkFile = (file, uuid) => {
  return new Promise(async (resolve, reject) => {
    let fileType = checkMediaType(file);
    if (fileType === "unknown") {
      message.info({
        position: "top",
        content: $$t("create.select_audio_or_video"),
      });
      modelDataStore.deleteUploadItem(uuid);
      reject("not audio");
      return;
    }

    if (file.size > 1024 * 1024 * 100) {
      message.info({
        position: "top",
        content: $$t("create.max_file_size"),
      });
      modelDataStore.deleteUploadItem(uuid);
      reject("max file size");
      return;
    }

    try {
      const duration = parseInt(await tools.getMediaFileDuration(file));
      if (duration > 0) {
        if (duration < minDuration || duration > maxDuration) {
          message.info({
            position: "top",
            content: $$t("create.model_file_duration", "", {
              min: minDuration,
              max: maxDuration,
            }),
          });
          modelDataStore.deleteUploadItem(uuid);
          reject("min file duration");
          return;
        } else {
          modelDataStore.updateFileList(uuid, {
            duration,
          });
          resolve({ file, uuid });
        }
      } else {
        message.info({
          position: "top",
          content: $$t("create.select_audio_or_video"),
        });
        modelDataStore.deleteUploadItem(uuid);
        reject("not audio");
        return;
      }
    } catch (e) {
      try {
        const outputfile = await ffmpegUtil.transFileToMp3(file);
        let duration = 0;
        if (outputfile) {
          duration = parseInt(
            await tools.getMediaFileDuration(outputfile.file)
          );
        }

        if (duration > 0) {
          if (duration < minDuration || duration > maxDuration) {
            message.info({
              position: "top",
              content: $$t("create.model_file_duration", "", {
                min: minDuration,
                max: maxDuration,
              }),
            });
            modelDataStore.deleteUploadItem(uuid);
            reject("min file duration");
          } else {
            modelDataStore.updateFileList(uuid, {
              duration,
              file: outputfile.file,
            });
            resolve({ file: outputfile.file, uuid });
          }
        } else {
          message.info({
            position: "top",
            content: $$t("create.select_audio_or_video"),
          });
          modelDataStore.deleteUploadItem(uuid);
          reject("not audio");
          return;
        }
      } catch (e) {
        message.info({
          position: "top",
          content:
            fileType === "video"
              ? $$t("create.select_video_error")
              : $$t("create.select_audio_error"),
        });
        reject("not audio");
      }
    }
  });
};

// 上传单个文件
const doUploadFile = async (file, uuid) => {
  try {
    // 如果文件不是 MP3 格式，转换为 MP3
    if (
      !(
        clipUtil.isMP3ByMimeType(file.type) ||
        clipUtil.isMP3ByExtension(file.name)
      )
    ) {
      try {
        const outputfile = await ffmpegUtil.transFileToMp3(file);
        file = outputfile.file;
      } catch {
        
      }
    }

    // 上传文件到对象存储
    const objectStorageRes = await objectStorage.uploadFileToObjectStorage(
      file
    );

    // 更新文件列表状态
    modelDataStore.updateFileList(uuid, {
      state: 2,
      url: objectStorageRes.pathKey,
    });

    // 返回上传结果
    return { success: true, result: objectStorageRes };
  } catch (error) {
    // 上传失败，更新文件列表状态
    modelDataStore.updateFileList(uuid, {
      is_fail: true,
    });

    // 返回错误信息
    return { success: false, error };
  }
};

const doLoadMore = (done) => {
  modelDataStore.doLoadMore(done);
};

// 初始化上传内容
onMounted(() => {
  nextTick(() => {
    initUploadContent();
  });
});
</script>
<style lang="scss">
.modelaudio-drag-upload {
  // height: 300px;
  height: 100%;
  .filepond--wrapper {
    height: 100%;
  }
  .filepond--root {
    // height: var(--filepond-height) !important;
    // min-height: var(--filepond-height) !important;
    height: 100% !important;
    min-height: 100% !important;
    background: transparent;
    box-shadow: none;
    // border-radius: var(--filepond-height);
    align-items: center;
    justify-content: center;
    // border-radius: var(--filepond-height);
    margin: 0;
    .filepond--wrapper {
      height: 100% !important;
      // border-radius: var(--filepond-height);
    }
    .filepond--browser.filepond--browser {
      height: 0;
    }
    .filepond--list-scroller {
      display: none;
    }
    .filepond--credits {
      display: none;
    }

    .filepond--panel-root {
      border-radius: 0.5em;
      background: transparent;
      box-shadow: none;
      // border-radius: var(--filepond-height);
      display: none;
    }

    .filepond--assistant {
      display: block;
    }
    .filepond--drop-label {
      display: block;
      visibility: visible !important;
      transform: translate3d(0px, 0, 0px) !important;
      height: 100% !important;
      // border-radius: var(--filepond-height) !important;
      height: 100% !important;
      opacity: 1 !important;
      pointer-events: auto !important;
      & > label {
        width: 0;
        height: 0;
        overflow: hidden;
        padding: 0;
      }
    }
    &[data-hopper-state="drag-over"] {
      .modelaudio-drag-upload-main {
        border: 1px dashed #94adff;
      }
    }
  }
  .modelaudio-drag-upload-main {
    height: 100%;
    box-sizing: border-box;
    // height: 300px;
    padding: 20px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    .empty-wrapper {
      box-sizing: border-box;
      height: 100%;
      display: flex;
      flex-direction: column;
    }
    .upload-title {
      img {
        width: 24px;
        height: 24px;
      }
      .text {
        color: #fff;
        margin-left: 4px;
        font-size: 18px;
        font-weight: bold;
        line-height: 24px;
        span {
          cursor: pointer;
          font-weight: 700;
          color: #72ffec;
        }
      }
    }
    .upload-issue {
      flex: 1;
      height: 0;
      overflow-y: auto;
      margin-top: 20px;
      color: #fff;
      font-size: 14px;
      line-height: 160%;
      .item {
        margin-bottom: 8px;
        &:last-of-type {
          margin-bottom: 0;
        }
        .name {
          width: 86px;
          font-weight: bold;
          flex-shrink: 0;
        }
        .desc {
          margin-left: 10px;
        }
      }
    }
    .upload-btn {
      height: 0;
      visibility: hidden;
    }
    .show-list {
      // width: 100%;
      // height: 100%;
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      align-content: flex-start;
      justify-content: flex-start;
      .card {
        width: 56px;
        height: 56px;
        &-add {
          // width: 56px;
          // height: 56px;
          background: rgba(148, 173, 255, 0.3);
          border-radius: 4px;
          img {
            width: 20px;
            height: 20px;
            display: block;
          }
        }
      }
    }
  }
}
.modelaudio-drag-upload--mobile {
  // --filepond-height: 100%;
  height: 100%;
  .filepond--wrapper {
    height: 100%;
  }
  .filepond--root {
    // height: var(--filepond-height) !important;
    // min-height: var(--filepond-height) !important;
    height: 100% !important;
    min-height: 100% !important;
    background: transparent;
    box-shadow: none;
    // border-radius: var(--filepond-height);
    align-items: center;
    justify-content: center;
    // border-radius: var(--filepond-height);
    margin: 0;
    .filepond--wrapper {
      height: 100% !important;
      // border-radius: var(--filepond-height);
    }
    .filepond--browser.filepond--browser {
      height: 0;
    }
    .filepond--list-scroller {
      display: none;
    }
    .filepond--credits {
      display: none;
    }

    .filepond--panel-root {
      border-radius: 0.5em;
      background: transparent;
      box-shadow: none;
      // border-radius: var(--filepond-height);
      display: none;
    }

    .filepond--assistant {
      display: block;
    }
    .filepond--drop-label {
      display: block;
      visibility: visible !important;
      transform: translate3d(0px, 0, 0px) !important;
      height: 100% !important;
      // border-radius: var(--filepond-height) !important;
      height: 100% !important;
      opacity: 1 !important;
      pointer-events: auto !important;
      & > label {
        width: 0;
        height: 0;
        overflow: hidden;
        padding: 0;
      }
    }
    &[data-hopper-state="drag-over"] {
      .modelaudio-drag-upload-main {
        border: 1px dashed #94adff;
      }
    }
  }
  .modelaudio-drag-upload-main {
    height: 100%;
    padding: 14px 12px;
    border-radius: 12px;
    .empty-wrapper {
      display: flex;
      flex-direction: column;
      height: 100%;
    }
    .upload-title {
      display: none;
    }
    .upload-issue {
      flex: 1;
      height: 0;
      overflow-y: auto;
      margin-top: 0;
      font-size: 12px;
    }
    .show-list {
      .card {
        &-add {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 8px;
        }
      }
    }
    .upload-btn {
      visibility: visible;
      margin-top: 12px;
      width: 100%;
      height: 36px;
      border-radius: 50px;
      color: #000;
      background: #fff;
    }
  }
}
</style>
  
